import type { MechtaIDProfile, MechtaIDUser } from '@mechta-dev/mechta-id-js'
import { MechtaID } from '@mechta-dev/mechta-id-js'
import { pinia } from 'boot/pinia'
import { useAuthStore } from 'stores/auth'

function configuredValue(
  runtimeValue: string | undefined,
  buildValue: string | undefined,
) {
  const value = runtimeValue?.trim() || buildValue?.trim()
  if (value) return value
  return ''
}

function required(name: string, value: string) {
  if (!value) throw new Error(`${name} is required`)
  return value
}

const authority = required(
  'OIDC_ISSUER',
  configuredValue(window.OIDC_ISSUER, import.meta.env.VITE_OIDC_ISSUER),
)
const clientId = required(
  'OIDC_CLIENT_ID',
  configuredValue(window.OIDC_CLIENT_ID, import.meta.env.VITE_OIDC_CLIENT_ID),
)
const configuredScope = configuredValue(
  window.OIDC_SCOPE,
  import.meta.env.VITE_OIDC_SCOPE,
) || 'openid profile'
// The profile page reads phone_number, which Mechta ID discloses only with the phone scope.
const scope = [...new Set([...configuredScope.split(/\s+/).filter(Boolean), 'phone'])].join(' ')

const redirectUri = `${window.location.origin}/auth/callback`
const postLogoutRedirectUri = `${window.location.origin}/`
const mechtaID = new MechtaID({
  authority,
  clientId,
  redirectUri,
  postLogoutRedirectUri,
  scope,
})

let initializePromise: Promise<boolean> | null = null

/** Действие перед выходом, пока токен ещё действует (отписка устройства от пушей и т.п.). */
export type BeforeLogoutHook = () => Promise<void> | void

// выход не ждёт действия дольше: сеть могла пропасть, а продавец нажал «Выйти»
const BEFORE_LOGOUT_TIMEOUT = 3000

function authStore() {
  return useAuthStore(pinia)
}

function safeReturnPath(value: string) {
  return value.startsWith('/') && !value.startsWith('//') ? value : '/'
}

function currentHashRoute() {
  const hash = window.location.hash
  return hash.startsWith('#/') ? safeReturnPath(hash.slice(1)) : '/'
}

function isAuthorizationCallback() {
  const url = new URL(window.location.href)
  const callbackPath = new URL(redirectUri).pathname
  return url.pathname === callbackPath
    && url.searchParams.has('state')
    && (url.searchParams.has('code') || url.searchParams.has('error'))
}

function restoreHashRoute(returnTo: string) {
  const target = new URL(window.location.origin)
  target.pathname = '/'
  target.hash = safeReturnPath(returnTo)
  window.history.replaceState({}, '', `${target.pathname}${target.search}${target.hash}`)
}

class MechtaIDAuthService {
  #beforeLogout = new Set<BeforeLogoutHook>()

  constructor() {
    mechtaID.onAuthRefreshSuccess = () => this.syncStore()
    mechtaID.onAuthLogout = () => authStore().$reset()
  }

  initialize(): Promise<boolean> {
    if (!initializePromise) {
      initializePromise = this.initializeSession()
    }
    return initializePromise
  }

  async login(returnTo = currentHashRoute()) {
    authStore().setAuthError('')
    await mechtaID.login({ returnTo: safeReturnPath(returnTo) })
  }

  async loginAsAnotherUser(returnTo = currentHashRoute()) {
    authStore().setAuthError('')
    await mechtaID.login({
      returnTo: safeReturnPath(returnTo),
      prompt: 'login',
    })
  }

  /**
   * Подписка на выход: hook выполняется в logout() до сброса сессии (запросы ещё с токеном).
   * Ошибка или зависание hook-а выход не останавливают. Возвращает отписку.
   */
  onBeforeLogout(hook: BeforeLogoutHook) {
    this.#beforeLogout.add(hook)
    return () => {
      this.#beforeLogout.delete(hook)
    }
  }

  async logout() {
    await this.#runBeforeLogout()
    authStore().$reset()
    try {
      await mechtaID.logout({ redirectUri: postLogoutRedirectUri })
    } catch (error) {
      await mechtaID.clearToken()
      throw error
    }
  }

  async getAccessToken() {
    await this.initialize()
    const token = await mechtaID.getToken()
    this.syncStore()
    return token
  }

  /**
   * Профиль текущего пользователя (claims из userinfo).
   * oidc-client-ts сконфигурирован с loadUserInfo: true и уже подмешал userinfo
   * в mechtaID.profile — читаем готовое, чтобы не делать второй такой же запрос.
   * loadUserInfo() — фолбэк на случай, если профиль почему-то не наполнен.
   */
  async getProfile(): Promise<MechtaIDProfile> {
    await this.initialize()
    return mechtaID.profile ?? mechtaID.loadUserInfo()
  }

  async refreshAccessToken() {
    await this.initialize()
    await mechtaID.updateToken(Number.MAX_SAFE_INTEGER)
    const token = await mechtaID.getToken(0)
    this.syncStore()
    return token
  }

  /**
   * Терминальный 401: refresh не помог или сервер не принял свежий токен.
   * Чистим сессию БЕЗ повторного входа — при живой SSO-сессии `login()` здесь
   * ушёл бы в бесконечный редирект (Mechta ID молча выдаёт такой же токен).
   * Экран входа открывает guard роутера или кнопка «Попробовать снова».
   * @param authError код причины для `AuthPage` (напр. `access_denied`).
   */
  async endSession(authError = '') {
    await mechtaID.clearToken()
    const store = authStore()
    store.clearSSO()
    if (authError) store.setAuthError(authError)
    initializePromise = Promise.resolve(false)
  }

  private async initializeSession() {
    const store = authStore()
    try {
      if (isAuthorizationCallback()) {
        const result = await mechtaID.handleCallback()
        store.setSSOUser(result.user)
        restoreHashRoute(result.returnTo)
      } else {
        await mechtaID.init({ restoreUrl: false })
        this.syncStore()
      }
      store.setAuthError('')
      return Boolean(store.token)
    } catch (error) {
      await mechtaID.clearToken()
      store.clearSSO()
      store.setAuthError(
        error instanceof Error ? error.message : 'Не удалось выполнить вход через Mechta ID',
      )
      if (isAuthorizationCallback()) restoreHashRoute('/')
      return false
    }
  }

  async #runBeforeLogout() {
    const hooks = [...this.#beforeLogout].map((hook) => Promise.resolve().then(hook))
    let timer: ReturnType<typeof setTimeout> | undefined
    const timeout = new Promise<void>((resolve) => {
      timer = setTimeout(resolve, BEFORE_LOGOUT_TIMEOUT)
    })
    await Promise.race([Promise.allSettled(hooks), timeout])
    clearTimeout(timer)
  }

  private syncStore(): MechtaIDUser | null {
    const user = mechtaID.user
    if (user) authStore().setSSOUser(user)
    else authStore().clearSSO()
    return user
  }
}

export const mechtaIDAuthService = new MechtaIDAuthService()
